import React from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

/* Match conversation attachment skeleton progress view from _new_messages.scss and messages.js */

const shimmerOverlay = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 16px 0;
  padding: 0 16px;
`;

const SkeletonMessage = styled.div`
  border-radius: 12px;
  padding: 16px;
  max-width: 70%;
  min-width: 200px;
`;

const ActionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #79d9d9;
  margin-bottom: 8px;
`;

const MessageText = styled.div`
  color: #333;
  font-size: 14px;
  margin-bottom: 12px;
`;

/* Dark purple container - same visual as conversation attachment skeleton (#4a2982) */
const AttachmentPlaceholder = styled.div`
  position: relative;
  width: 250px;
  height: 180px;
  background: #4a2982;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SkeletonShimmer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${shimmerOverlay} 2s infinite;
  z-index: 0;
  pointer-events: none;
`;

/* Font Awesome spinner - same as conversation: .skeleton-spinner color #79D9D9 */
const SpinnerWrapper = styled.div`
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 32px;
  color: #79d9d9;
  z-index: 3;
`;

/* Rounded pill: "Uploading video... 40%" - light gray background, percent in teal */
const ProgressTextPill = styled.div`
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translateX(-50%);
  color: #333;
  font-size: 14px;
  font-weight: 600;
  z-index: 3;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 3px;
`;

const ProgressPercent = styled.span`
  color: #79d9d9;
  font-weight: 700;
  margin-left: 4px;
`;

/* Progress bar at bottom - darker purple-gray track (#6a46a6) */
const ProgressBar = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  height: 6px;
  background: #6a46a6;
  border-radius: 4px;
  overflow: hidden;
  z-index: 3;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.15);
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #79d9d9 0%, #6ac9c9 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${(props) => Math.max(props.progress, 2)}%;
  min-width: 2%;
  box-shadow: 0 2px 4px rgba(121, 217, 217, 0.5);
`;

const FileName = styled.div`
  font-size: 12px;
  color: #333;
  margin-top: 8px;
  word-break: break-all;
`;

const MessageSkeleton = ({ action, message, attachmentName, progress }) => {
  const label = action.replace(/_/g, " ");
  const isProcessing = progress >= 100;
  const statusText = isProcessing ? "Processing..." : "Uploading video...";

  return (
    <SkeletonWrapper>
      <SkeletonMessage>
        <ActionLabel>{label}</ActionLabel>
        {message && <MessageText>{message}</MessageText>}
        <AttachmentPlaceholder>
          <SkeletonShimmer />
          <SpinnerWrapper>
            <FontAwesomeIcon icon={faSpinner} spin />
          </SpinnerWrapper>
          <ProgressTextPill>
            {statusText}{" "}
            <ProgressPercent>{isProcessing ? "100" : Math.round(progress)}%</ProgressPercent>
          </ProgressTextPill>
          <ProgressBar>
            <ProgressFill progress={progress} />
          </ProgressBar>
        </AttachmentPlaceholder>
        {attachmentName && <FileName>{attachmentName}</FileName>}
      </SkeletonMessage>
    </SkeletonWrapper>
  );
};

MessageSkeleton.propTypes = {
  action: PropTypes.string.isRequired,
  message: PropTypes.string,
  attachmentName: PropTypes.string,
  progress: PropTypes.number,
};

MessageSkeleton.defaultProps = {
  message: "",
  attachmentName: "",
  progress: 0,
};

export default MessageSkeleton;
